import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Components */
import { Icon } from "../../components"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* REST API */
import { groupSave, groupStatus, groupOrder, groupRemove } from "../../api/Category"

/* Widgets */
import { toast, confirm } from "../common"

/* Shared */
import { plural, toneOf } from "./shared"

Modal.setAppElement("#root")


/* Widget Groups Modal — the sections of the home screen of the app. The line is the order the
   app shows them in: a group is dragged by its handle or moved by the arrows. A group with categories is removed
   only together with the group which takes them. Every change is
   saved at once and the page gets the fresh line through onChanged(groups, reload).
   It is opened by the reference: this._groups.current.open() */
class GroupsModal extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,

            /* The id of the group whose name is being written, and the name itself */
            editing: null,
            editName: "",

            /* A new group */
            name: "",

            /* The group being dragged and the one it is held over */
            dragging: null,
            over: null,

            /* The group being removed with its categories, and the group which takes them */
            removing: null,
            moveTo: null,

            /* The id of the group a request is on its way for, "new" for a new one, "order" for the line */
            waiting: null
        }
    }

    /* Open the window */
    open = () => {
        this.setState({ visible: true, editing: null, editName: "", name: "", dragging: null, over: null, removing: null, moveTo: null, waiting: null })
    }

    /* Close the window */
    close = () => {
        this.setState({ visible: false })
    }

    /* Every answer of the groups gives back the whole fresh line */
    apply = (response, success) => {

        if (response && response.status === 200) {
            this.props.onChanged(response.data)

            if (success) {
                toast.success(success)
            }

            return true
        }

        if (response && response.data && response.data.code === "GROUP_NOT_EMPTY") {
            toast.error("Выберите группу, в которую перенести категории")
            return false
        }

        if (response && response.data && response.data.code === "TARGET_NOT_FOUND") {
            toast.error("Группа для переноса не найдена — откройте окно заново")
            return false
        }

        toast.error("Что-то пошло не так! Попробуйте позднее")
        return false
    }

    /* Create a group */
    create = event => {

        if (event) {
            event.preventDefault()
        }

        /* Fields */
        const { name, waiting } = this.state

        if (waiting || name.trim().length === 0) {
            return
        }

        this.setState({ waiting: "new" })

        groupSave({ name: name.trim() }).then(response => {
            if (this.apply(response, "Группа создана")) {
                this.setState({ name: "" })
            }
        }).catch(() => this.apply(null)).finally(() => this.setState({ waiting: null }))
    }

    /* Start writing the name of a group */
    edit = group => {
        this.setState({ editing: group.id, editName: group.name })
    }

    /* Save the new name — an empty or unchanged one just closes the line */
    rename = group => {

        /* Fields */
        const { editName, editing, waiting } = this.state

        /* Enter and the lost focus both come here — the name is saved once */
        if (editing !== group.id || waiting === group.id) {
            return
        }

        if (editName.trim().length === 0 || editName.trim() === group.name) {
            this.setState({ editing: null })
            return
        }

        this.setState({ waiting: group.id })

        groupSave({ id: group.id, name: editName.trim() }).then(response => {
            if (this.apply(response, "Группа переименована")) {
                this.setState({ editing: null })
            }
        }).catch(() => this.apply(null)).finally(() => this.setState({ waiting: null }))
    }

    /* Show the group in the app or hide it */
    toggle = group => {

        if (this.state.waiting) {
            return
        }

        const value = parseInt(group.visible) === 1 ? 0 : 1

        this.setState({ waiting: group.id })

        groupStatus({ id: group.id, value }).then(response => {
            this.apply(response, value === 1 ? "Группа показана в приложении" : "Группа скрыта")
        }).catch(() => this.apply(null)).finally(() => this.setState({ waiting: null }))
    }

    /* Remove a group. An empty one goes after a question, one with categories first opens the
       choice of the group which takes them */
    remove = group => {

        if (this.state.waiting) {
            return
        }

        if (parseInt(group.count) > 0) {
            this.setState({ removing: group.id, moveTo: null, editing: null })
            return
        }

        confirm({
            title: `Удалить группу «${group.name}»?`,
            text: "В ней нет категорий, приложение её просто перестанет показывать",
            confirmText: "Да, удалить",
            cancelText: "Нет",
            danger: true
        }).then(confirmed => {
            if (confirmed) {
                this.destroy(group, null)
            }
        })
    }

    /* Send the removal — with the group which takes the categories, if there are any */
    destroy = (group, moveTo) => {

        this.setState({ waiting: group.id })

        const target = moveTo ? this.props.groups.find(item => item.id === moveTo) : null
        const success = target ? `Группа удалена, категории перенесены в «${target.name}»` : "Группа удалена"

        groupRemove({ id: group.id, moveTo }).then(response => {
            if (this.apply(response, success)) {
                this.setState({ removing: null, moveTo: null })
            }
        }).catch(() => this.apply(null)).finally(() => this.setState({ waiting: null }))
    }

    /* The choice under a group with categories — where they go before it is removed */
    _removing = group => {

        /* Fields */
        const { moveTo, waiting } = this.state
        const { groups } = this.props

        const count = parseInt(group.count) || 0
        const others = groups.filter(item => item.id !== group.id)

        if (others.length === 0) {
            return (
                <div className="groups-modal-remove">
                    <div className="groups-modal-remove-title">Это единственная группа — категориям некуда переехать. Создайте ещё одну группу.</div>
                    <div className="groups-modal-remove-buttons">
                        <div onClick={() => this.setState({ removing: null })} className="groups-modal-remove-button light">Понятно</div>
                    </div>
                </div>
            )
        }

        return (
            <div className="groups-modal-remove">

                <div className="groups-modal-remove-title">
                    В группе <b>{count} {plural(count, "категория", "категории", "категорий")}</b> — куда их перенести?
                </div>

                <div className="groups-modal-remove-groups">
                    {others.map(item => {

                        const tone = toneOf(item.id)

                        return (
                            <div
                                key={`${item.id}`}
                                onClick={() => this.setState({ moveTo: item.id })}
                                style={{ "--tone": tone.accent, "--tone-soft": tone.soft }}
                                className={`category-modal-group ${moveTo === item.id ? "active" : ""} ${parseInt(item.visible) === 1 ? "" : "hidden"}`}
                            >
                                <i />
                                {item.name}
                            </div>
                        )
                    })}
                </div>

                <div className="groups-modal-remove-buttons">
                    <div onClick={() => this.setState({ removing: null, moveTo: null })} className="groups-modal-remove-button light">Отмена</div>
                    <div onClick={() => moveTo && waiting !== group.id && this.destroy(group, moveTo)} className={`groups-modal-remove-button danger ${!moveTo || waiting === group.id ? "disabled" : ""}`}>
                        {waiting === group.id ? <LoadingOutlined /> : "Перенести и удалить"}
                    </div>
                </div>

            </div>
        )
    }

    /* Put the line in a new order and save it */
    order = list => {

        /* The line is redrawn at once and comes back if the server did not take it */
        const previous = this.props.groups

        this.props.onChanged(list.map((group, index) => ({ ...group, queue: index + 1 })), false)
        this.setState({ waiting: "order" })

        groupOrder({ ids: list.map(group => group.id) }).then(response => {
            if (!this.apply(response)) {
                this.props.onChanged(previous, false)
            }
        }).catch(() => {
            this.apply(null)
            this.props.onChanged(previous, false)
        }).finally(() => this.setState({ waiting: null }))
    }

    /* Move a group one step up or down */
    move = (index, step) => {

        const list = [...this.props.groups]
        const target = index + step

        if (target < 0 || target >= list.length || this.state.waiting) {
            return
        }

        const [group] = list.splice(index, 1)
        list.splice(target, 0, group)

        this.order(list)
    }

    /* Drop the dragged group on the place of another */
    drop = targetID => {

        /* Fields */
        const { dragging } = this.state

        this.setState({ dragging: null, over: null })

        if (!dragging || dragging === targetID || this.state.waiting) {
            return
        }

        const list = [...this.props.groups]
        const from = list.findIndex(group => group.id === dragging)
        const to = list.findIndex(group => group.id === targetID)

        if (from < 0 || to < 0) {
            return
        }

        const [group] = list.splice(from, 1)
        list.splice(to, 0, group)

        this.order(list)
    }

    /* One group of the line */
    _group = (group, index) => {

        /* Fields */
        const { editing, editName, dragging, over, removing, waiting } = this.state
        const { groups } = this.props

        const tone = toneOf(group.id)
        const shown = parseInt(group.visible) === 1
        const count = parseInt(group.count) || 0
        const busy = waiting === group.id

        return (
            <div
                key={`${group.id}`}
                draggable={editing !== group.id && removing !== group.id}
                onDragStart={event => { event.dataTransfer.effectAllowed = "move"; this.setState({ dragging: group.id }) }}
                onDragOver={event => { event.preventDefault(); if (over !== group.id) this.setState({ over: group.id }) }}
                onDragEnd={() => this.setState({ dragging: null, over: null })}
                onDrop={() => this.drop(group.id)}
                style={{ "--tone": tone.accent, "--tone-soft": tone.soft }}
                className={`groups-modal-item ${shown ? "" : "hidden"} ${dragging === group.id ? "dragging" : ""} ${over === group.id && dragging !== group.id ? "over" : ""} ${removing === group.id ? "removing" : ""}`}
            >

                <div className="groups-modal-grip" title="Перетащите">
                    <Icon name="grip" size={18} width={3} />
                </div>

                <div className="groups-modal-number">{index + 1}</div>

                <div className="groups-modal-body">
                    {editing === group.id
                        ? <form onSubmit={event => { event.preventDefault(); this.rename(group) }} className="groups-modal-edit">
                            <input
                                autoFocus
                                maxLength={100}
                                value={editName}
                                onChange={event => this.setState({ editName: event.target.value })}
                                onBlur={() => this.rename(group)}
                                onKeyDown={event => { if (event.key === "Escape") this.setState({ editing: null }) }}
                            />
                        </form>
                        : <div onClick={() => this.edit(group)} className="groups-modal-name" title="Переименовать">
                            {group.name}
                            <Icon name="edit" size={13} width={2} />
                        </div>
                    }
                    <div className="groups-modal-meta">
                        {count === 0
                            ? "Пустая"
                            : `${count} ${plural(count, "категория", "категории", "категорий")}`
                        }
                        {!shown && <em>скрыта в приложении</em>}
                    </div>
                </div>

                <div className="groups-modal-actions">

                    {busy && removing !== group.id && <div className="groups-modal-action"><LoadingOutlined /></div>}

                    <div onClick={() => this.move(index, -1)} className={`groups-modal-action ${index === 0 ? "disabled" : ""}`} title="Выше">
                        <Icon name="up" size={16} width={2.2} />
                    </div>

                    <div onClick={() => this.move(index, 1)} className={`groups-modal-action ${index === groups.length - 1 ? "disabled" : ""}`} title="Ниже">
                        <Icon name="down" size={16} width={2.2} />
                    </div>

                    <div onClick={() => this.toggle(group)} className={`groups-modal-action ${shown ? "" : "off"}`} title={shown ? "Скрыть в приложении" : "Показать в приложении"}>
                        <Icon name={shown ? "eye" : "eyeOff"} size={16} width={2} />
                    </div>

                    <div onClick={() => this.remove(group)} className={`groups-modal-action danger ${removing === group.id ? "active" : ""}`} title="Удалить">
                        <Icon name="trash" size={16} width={2} />
                    </div>

                </div>

                {removing === group.id && this._removing(group)}

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, name, waiting } = this.state
        const { groups } = this.props

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="wide-modal"
                overlayClassName="wide-modal-overlay"
                closeTimeoutMS={200}
            >

                <div className="wide-modal-header">
                    <h2>Группы категорий</h2>
                    <div onClick={this.close} className="wide-modal-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>
                </div>

                <div className="wide-modal-body">

                    <div className="wide-modal-hint groups-modal-hint">
                        Разделы главной страницы приложения. Порядок здесь — порядок в приложении: перетащите группу
                        за ручку или двигайте стрелками. Категории удаляемой группы переезжают в группу, которую вы выберете.
                    </div>

                    <div className="groups-modal-list">
                        {groups.map((group, index) => this._group(group, index))}
                    </div>

                    <form onSubmit={this.create} className="groups-modal-new">
                        <div className="groups-modal-new-icon">
                            <Icon name="plus" size={16} width={2.4} />
                        </div>
                        <input placeholder="Новая группа, например «Массаж»" maxLength={100} value={name} onChange={event => this.setState({ name: event.target.value })} />
                        <button type="submit" disabled={name.trim().length === 0 || waiting === "new"}>
                            {waiting === "new" ? <LoadingOutlined /> : "Добавить"}
                        </button>
                    </form>

                </div>

            </Modal>
        )
    }
}

export default GroupsModal
